import {z} from 'zod';

/**
 * Schema for raw OpenAI extraction result before sanitization and conversion.
 *
 * This schema represents the exact structure that comes from OpenAI's response
 * for the extractDeliveryNoteDocument use case. The key difference from the
 * final result schema is that discount values are strings (e.g., "15%", "43.22%")
 * as returned by OpenAI, rather than decimal numbers.
 *
 * @ai-guidance
 * This schema should be used:
 * 1. As the response_format for OpenAI structured output (zodResponseFormat)
 * 2. For validating raw OpenAI responses before sanitization
 * 3. As input to sanitizeExtractionResult function
 *
 * After sanitization and conversion, the result should match ExtractDeliveryNoteDocumentResult
 * where discount values are converted from strings like "15%" to decimal numbers like 0.15.
 *
 * @see ExtractDeliveryNoteDocumentResult - The final schema after conversion
 * @see convertPercentageToDecimal - Utility for converting discount strings to numbers
 */

const extractDeliveryNoteDocumentBasicInformationSchema = z.object({
  /**
   * Supplier name extracted from the document
   */
  supplier: z.string().nullable(),

  /**
   * Delivery note number
   */
  deliveryNoteNumber: z.string().nullable(),

  /**
   * Invoice number if present
   */
  invoiceNumber: z.string().nullable(),

  /**
   * Currency code (e.g., "CZK", "EUR")
   */
  currency: z.string().nullable(),
});

export const extractDeliveryNoteDocumentItemSchema = z.object({
  /**
   * Catalogue/part number of the item
   */
  catalogueNumber: z.string().nullable(),

  /**
   * Item name/description
   */
  name: z.string().nullable(),

  /**
   * Quantity of items
   */
  quantity: z.number().nullable(),

  /**
   * Unit of measurement (e.g., "ks", "kg")
   */
  unit: z.string().nullable(),

  /**
   * VAT percentage as string (e.g., "21", "0")
   */
  vat: z.string().nullable(),

  /**
   * Discount percentage as STRING (e.g., "15%", "43.22%", "25.00%")
   * This will be converted to decimal number in the final result
   */
  discount: z.string().nullable(),

  /**
   * Price per unit
   */
  unitPrice: z.number().nullable(),

  /**
   * Total price for this line item
   */
  totalPrice: z.number().nullable(),
});

const extractDeliveryNoteDocumentListOfItemsSchema = z.object({
  /**
   * Array of items found on this page
   */
  items: z.array(extractDeliveryNoteDocumentItemSchema),

  /**
   * Page number where these items were found
   */
  pageNumber: z.number(),
});

/**
 * Complete schema for OpenAI extraction result before sanitization.
 * Discount values are strings as returned by OpenAI (e.g., "15%", "43.22%").
 */
export const extractDeliveryNoteDocumentOpenAIResultSchema = z.object({
  /**
   * Basic document information
   */
  basicInformation: extractDeliveryNoteDocumentBasicInformationSchema,

  /**
   * List of items grouped by page
   */
  listOfItems: z.array(extractDeliveryNoteDocumentListOfItemsSchema),
});

/**
 * TypeScript type for OpenAI extraction result before sanitization
 */
export type ExtractDeliveryNoteDocumentOpenAIResult = z.infer<
  typeof extractDeliveryNoteDocumentOpenAIResultSchema
>;
